import React, { useState, useMemo } from 'react';
import {
  ArrowRight,
  Clock,
  Briefcase,
  GraduationCap,
  Users,
  Home,
  FileText,
  DollarSign,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Calendar,
  Building,
  Heart,
  Award,
  Target,
  Info
} from 'lucide-react';
import './IrelandStampVisualizer.css';

// Stamp Types Data
const STAMP_DATA = {
  'stamp-2': {
    id: 'stamp-2',
    name: 'Stamp 2',
    fullName: 'Student permission (Stamp 2)',
    icon: GraduationCap,
    color: '#3b82f6',
    duration: 'Duration of course (1-4 years)',
    description: 'For full-time degree students at recognized Irish institutions',
    workRights: {
      allowed: true,
      hours: '20 hours/week during term, 40 hours during holidays',
      details: 'Can work part-time without additional permit'
    },
    studyRights: {
      allowed: true,
      details: 'Full-time degree program at recognized institution'
    },
    familyReunification: {
      allowed: false,
      details: 'Dependents not typically permitted'
    },
    pathToNext: 'Graduate Scheme (Stamp 1G) after successful completion',
    requiredDocuments: [
      'College acceptance letter',
      'Proof of tuition payment',
      'Financial evidence (€7,000+ per year)',
      'Private medical insurance',
      'English proficiency proof',
      'Academic transcripts'
    ],
    fees: '€60 (registration)',
    processingTime: '4-8 weeks',
    nextStamps: ['stamp-1g'],
    timeline: { start: 0, end: 4 }
  },
  'stamp-1g': {
    id: 'stamp-1g',
    name: 'Stamp 1G',
    fullName: 'Graduate Scheme',
    icon: Award,
    color: '#10b981',
    duration: '24 months (2 years)',
    description: 'For recent graduates from Irish institutions to seek employment',
    workRights: {
      allowed: true,
      hours: 'Full-time (40 hours/week)',
      details: 'Can work without employment permit in any role'
    },
    studyRights: {
      allowed: false,
      details: 'Cannot pursue full-time education'
    },
    familyReunification: {
      allowed: false,
      details: 'Dependents not permitted'
    },
    pathToNext: 'Employment Permit (Stamp 1) or Critical Skills Permit',
    requiredDocuments: [
      'Degree certificate or letter of completion',
      'GNIB card from student visa',
      'Passport',
      'Proof of address',
      'Bank statements'
    ],
    fees: '€300',
    processingTime: '2-4 weeks',
    nextStamps: ['stamp-1', 'stamp-4-critical'],
    timeline: { start: 4, end: 6 }
  },
  'stamp-1': {
    id: 'stamp-1',
    name: 'Stamp 1',
    fullName: 'Employment Permit Holder',
    icon: Briefcase,
    color: '#8b5cf6',
    duration: '2 years (renewable)',
    description: 'For those with General Employment Permit or other work permits',
    workRights: {
      allowed: true,
      hours: 'Full-time with specified employer only',
      details: 'Tied to specific employer on permit'
    },
    studyRights: {
      allowed: false,
      details: 'Cannot pursue full-time education'
    },
    familyReunification: {
      allowed: true,
      details: 'Spouse and children can apply for Stamp 3 after 1 year'
    },
    pathToNext: 'Stamp 4 (Long Term Residency) after 5 years',
    requiredDocuments: [
      'Employment Permit',
      'Job offer/contract',
      'Employer letter',
      'Qualifications',
      'Salary evidence meeting the current role-specific permit threshold',
      'Labour Market Needs Test evidence'
    ],
    fees: '€500-€1,000 (permit) + €300 (visa)',
    processingTime: '8-12 weeks',
    nextStamps: ['stamp-4'],
    timeline: { start: 6, end: 11 }
  },
  'stamp-4-critical': {
    id: 'stamp-4-critical',
    name: 'Stamp 4',
    fullName: 'Stamp 4 after Critical Skills employment',
    icon: Target,
    color: '#f59e0b',
    duration: '2 years',
    description: 'A Stamp 4 pathway may follow qualifying Critical Skills employment; the permit and immigration permission are separate.',
    workRights: {
      allowed: true,
      hours: 'Full-time',
      details: 'Can change employer after 12 months'
    },
    studyRights: {
      allowed: false,
      details: 'Cannot pursue full-time education'
    },
    familyReunification: {
      allowed: true,
      details: 'Spouse eligible for Stamp 1G/3 immediately'
    },
    pathToNext: 'Stamp 4 automatically after 2 years',
    requiredDocuments: [
      'Critical Skills Employment Permit',
      'Job offer in critical skills occupation',
      'Relevant qualifications',
      'Salary evidence meeting the current Critical Skills threshold for the role'
    ],
    fees: '€1,000 (permit) + €300 (visa)',
    processingTime: '4-8 weeks',
    nextStamps: ['stamp-4'],
    timeline: { start: 6, end: 8 }
  },
  'stamp-3': {
    id: 'stamp-3',
    name: 'Stamp 3',
    fullName: 'Dependent/Spouse Visa',
    icon: Heart,
    color: '#ec4899',
    duration: 'Same as sponsor\'s permit',
    description: 'For spouses and dependents of employment permit holders',
    workRights: {
      allowed: false,
      hours: 'N/A',
      details: 'Cannot work without own employment permit'
    },
    studyRights: {
      allowed: true,
      details: 'Can study full-time'
    },
    familyReunification: {
      allowed: false,
      details: 'Cannot sponsor other family members'
    },
    pathToNext: 'Stamp 4 when sponsor gets Stamp 4, or own employment permit',
    requiredDocuments: [
      'Marriage certificate (for spouse)',
      'Birth certificates (for children)',
      'Sponsor\'s permit copy',
      'Proof of relationship',
      'Financial evidence'
    ],
    fees: '€300',
    processingTime: '4-8 weeks',
    nextStamps: ['stamp-4'],
    timeline: { start: 7, end: 12 }
  },
  'stamp-4': {
    id: 'stamp-4',
    name: 'Stamp 4',
    fullName: 'Stamp 4 permission',
    icon: Home,
    color: '#169B62',
    duration: '5 years (renewable)',
    description: 'Long-term residency with full access to labor market',
    workRights: {
      allowed: true,
      hours: 'Full-time',
      details: 'No employment permit needed, can work any job'
    },
    studyRights: {
      allowed: true,
      details: 'Can study full-time'
    },
    familyReunification: {
      allowed: true,
      details: 'Spouse and children eligible for Stamp 3'
    },
    pathToNext: 'Stamp 5 (permission without condition as to time) or citizenship',
    requiredDocuments: [
      '5 years of legal residence proof',
      'Previous permits and GNIB cards',
      'Employment history',
      'Tax clearance certificates',
      'Character references'
    ],
    fees: '€500',
    processingTime: '6-12 months',
    nextStamps: ['stamp-5', 'citizenship'],
    timeline: { start: 8, end: 13 }
  },
  'stamp-5': {
    id: 'stamp-5',
    name: 'Stamp 5',
    fullName: 'Stamp 5 (without condition as to time)',
    icon: Building,
    color: '#06b6d4',
    duration: 'Without condition as to time (subject to current registration rules)',
    description: 'Permission without a time condition; distinct from Irish citizenship and subject to current immigration rules',
    workRights: {
      allowed: true,
      hours: 'Full-time',
      details: 'Full labor market access'
    },
    studyRights: {
      allowed: true,
      details: 'Full education access'
    },
    familyReunification: {
      allowed: true,
      details: 'Family reunification rights'
    },
    pathToNext: 'Irish citizenship, subject to the naturalisation residence rules',
    requiredDocuments: [
      '8 years of legal residence proof',
      '5 years of Stamp 4',
      'Tax records for all years',
      'Character references',
      'Garda clearance'
    ],
    fees: '€175',
    processingTime: '6-18 months',
    nextStamps: ['citizenship'],
    timeline: { start: 13, end: 18 }
  },
  'citizenship': {
    id: 'citizenship',
    name: 'Citizenship',
    fullName: 'Irish Citizenship',
    icon: Award,
    color: '#FF8833',
    duration: 'Permanent',
    description: 'Irish passport and full citizenship rights',
    workRights: {
      allowed: true,
      hours: 'Unlimited',
      details: 'Full rights as Irish citizen'
    },
    studyRights: {
      allowed: true,
      details: 'Full rights including EU fees'
    },
    familyReunification: {
      allowed: true,
      details: 'Can sponsor family for visas'
    },
    pathToNext: 'N/A - Final step',
    requiredDocuments: [
      '5 years of residence (including 1 year continuous)',
      'Tax clearance',
      'Character references',
      'Citizenship application form',
      'Proof of integration'
    ],
    fees: '€1,125 (adults), €200 (minors)',
    processingTime: '12-24 months',
    nextStamps: [],
    timeline: { start: 13, end: 20 }
  }
};

// Transition Requirements
const TRANSITION_REQUIREMENTS = {
  'stamp-2-to-stamp-1g': {
    from: 'stamp-2',
    to: 'stamp-1g',
    requirements: [
      'Successfully complete degree program',
      'Apply within 6 months of graduation',
      'Valid GNIB card',
      'No immigration violations'
    ],
    documents: [
      'Degree certificate',
      'College completion letter',
      'Current GNIB card',
      'Passport'
    ],
    processingTime: '2-4 weeks',
    difficulty: 'Easy'
  },
  'stamp-1g-to-stamp-1': {
    from: 'stamp-1g',
    to: 'stamp-1',
    requirements: [
      'Job offer from Irish employer',
      'Salary meets the current role-specific employment-permit threshold',
      'Role qualifies for employment permit',
      'Labour Market Needs Test (for General EP)'
    ],
    documents: [
      'Employment contract',
      'Employer letter',
      'Qualifications',
      'Salary evidence meeting the current role-specific threshold'
    ],
    processingTime: '8-12 weeks',
    difficulty: 'Medium'
  },
  'stamp-1g-to-stamp-4-critical': {
    from: 'stamp-1g',
    to: 'stamp-4-critical',
    requirements: [
      'Job offer in critical skills occupation',
      'Salary meets the current Critical Skills threshold for the role',
      'Relevant qualifications for the role',
      'Employer registered in Ireland'
    ],
    documents: [
      'Critical Skills Employment Permit application',
      'Job offer letter',
      'Qualification certificates',
      'CV/Resume'
    ],
    processingTime: '4-8 weeks',
    difficulty: 'Medium'
  },
  'stamp-1-to-stamp-4': {
    from: 'stamp-1',
    to: 'stamp-4',
    requirements: [
      '5 years on employment permit',
      'Continuous employment',
      'Good character',
      'Tax compliance'
    ],
    documents: [
      '5 years of permits',
      'Employment history',
      'Tax clearance',
      'References'
    ],
    processingTime: '6-12 months',
    difficulty: 'Medium'
  },
  'stamp-4-critical-to-stamp-4': {
    from: 'stamp-4-critical',
    to: 'stamp-4',
    requirements: [
      '2 years on Critical Skills Permit',
      'Still employed',
      'Good character'
    ],
    documents: [
      'Critical Skills Permit',
      'Employment evidence',
      'Current Stamp 4 (from CSEP)'
    ],
    processingTime: 'Automatic',
    difficulty: 'Easy'
  },
  'stamp-4-to-stamp-5': {
    from: 'stamp-4',
    to: 'stamp-5',
    requirements: [
      '8 years total residence',
      '5 years on Stamp 4',
      'Good character',
      'Tax compliance'
    ],
    documents: [
      'Residence history',
      'Tax records',
      'Character references',
      'Garda clearance'
    ],
    processingTime: '6-18 months',
    difficulty: 'Hard'
  },
  'stamp-4-to-citizenship': {
    from: 'stamp-4',
    to: 'citizenship',
    requirements: [
      '5 years total residence (including 1 year continuous)',
      'Good character',
      'Tax compliance',
      'Intent to continue living in Ireland'
    ],
    documents: [
      'Residence proof',
      'Tax clearance',
      'Character references',
      'Application form'
    ],
    processingTime: '12-24 months',
    difficulty: 'Hard'
  },
  'stamp-5-to-citizenship': {
    from: 'stamp-5',
    to: 'citizenship',
    requirements: [
      '5 years residence after Stamp 5',
      'Good character',
      'Tax compliance'
    ],
    documents: [
      'Stamp 5 certificate',
      'Residence proof',
      'Tax records',
      'Application form'
    ],
    processingTime: '12-24 months',
    difficulty: 'Medium'
  }
};

// Timeline milestones
const TIMELINE_MILESTONES = [
  { year: 0, label: 'Start Studies', stamp: 'stamp-2' },
  { year: 2, label: 'Continue Studies', stamp: 'stamp-2' },
  { year: 4, label: 'Graduate', stamp: 'stamp-1g' },
  { year: 5, label: 'Find Job', stamp: 'stamp-1g' },
  { year: 6, label: 'Get Work Permit', stamp: 'stamp-1' },
  { year: 8, label: 'Critical Skills Path', stamp: 'stamp-4-critical' },
  { year: 10, label: 'Stamp 4 permission', stamp: 'stamp-4' },
  { year: 13, label: 'Stamp 5 permission', stamp: 'stamp-5' },
  { year: 15, label: 'Citizenship Eligible', stamp: 'citizenship' }
];

const IrelandStampVisualizer = () => {
  const [selectedStamp, setSelectedStamp] = useState(null);
  const [currentStamp, setCurrentStamp] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [timelineYear, setTimelineYear] = useState(0);
  const [showTransition, setShowTransition] = useState(null);

  // Calculate path from current stamp
  const calculatedPath = useMemo(() => {
    if (!currentStamp) return [];
    
    const path = [currentStamp];
    let current = currentStamp;
    
    while (STAMP_DATA[current]?.nextStamps?.length > 0) {
      const next = STAMP_DATA[current].nextStamps[0];
      path.push(next);
      current = next;
    }
    
    return path;
  }, [currentStamp]);

  // Calculate years to PR/Citizenship
  const yearsToPR = useMemo(() => {
    if (!currentStamp) return null;
    
    const currentTimeline = STAMP_DATA[currentStamp]?.timeline;
    if (!currentTimeline) return null;
    
    const yearsToStamp4 = Math.max(0, 8 - currentTimeline.start);
    const yearsToPR = Math.max(0, 13 - currentTimeline.start);
    const yearsToCitizenship = Math.max(0, 13 - currentTimeline.start);
    
    return { yearsToStamp4, yearsToPR, yearsToCitizenship };
  }, [currentStamp]);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const renderStampCard = (stampId, isPath = false, isActive = false) => {
    const stamp = STAMP_DATA[stampId];
    if (!stamp) return null;
    
    const IconComponent = stamp.icon;
    
    return (
      <div
        key={stampId}
        className={`stamp-card ${isPath ? 'path-card' : ''} ${isActive ? 'active' : ''} ${selectedStamp === stampId ? 'selected' : ''}`}
        style={{ '--stamp-color': stamp.color }}
        onClick={() => setSelectedStamp(stampId)}
      >
        <div className="stamp-icon" style={{ backgroundColor: `${stamp.color}20`, color: stamp.color }}>
          <IconComponent size={24} />
        </div>
        <div className="stamp-info">
          <h4 style={{ color: stamp.color }}>{stamp.name}</h4>
          <p className="stamp-full-name">{stamp.fullName}</p>
          <p className="stamp-duration">
            <Clock size={14} />
            {stamp.duration}
          </p>
        </div>
        {isPath && <ArrowRight className="path-arrow" size={20} />}
      </div>
    );
  };

  const renderStampDetails = () => {
    if (!selectedStamp) return null;
    
    const stamp = STAMP_DATA[selectedStamp];
    const IconComponent = stamp.icon;
    
    return (
      <div className="stamp-details">
        <div className="details-header" style={{ borderColor: stamp.color }}>
          <div className="details-icon" style={{ backgroundColor: `${stamp.color}20`, color: stamp.color }}>
            <IconComponent size={32} />
          </div>
          <div className="details-title">
            <h3 style={{ color: stamp.color }}>{stamp.name}</h3>
            <p>{stamp.fullName}</p>
          </div>
          <button className="close-btn" onClick={() => setSelectedStamp(null)}>×</button>
        </div>
        
        <p className="details-description">{stamp.description}</p>
        
        {/* Work Rights */}
        <div className="details-section">
          <button className="section-toggle" onClick={() => toggleSection('work')}>
            <Briefcase size={18} />
            Work Rights
            {expandedSection === 'work' ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          {expandedSection === 'work' && (
            <div className="section-content">
              <div className={`status-badge ${stamp.workRights.allowed ? 'allowed' : 'not-allowed'}`}>
                {stamp.workRights.allowed ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                {stamp.workRights.allowed ? 'Allowed' : 'Not Allowed'}
              </div>
              <p>{stamp.workRights.details}</p>
              {stamp.workRights.allowed && (
                <p className="hours-info"><Clock size={14} /> {stamp.workRights.hours}</p>
              )}
            </div>
          )}
        </div>
        
        {/* Study Rights */}
        <div className="details-section">
          <button className="section-toggle" onClick={() => toggleSection('study')}>
            <GraduationCap size={18} />
            Study Rights
            {expandedSection === 'study' ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          {expandedSection === 'study' && (
            <div className="section-content">
              <div className={`status-badge ${stamp.studyRights.allowed ? 'allowed' : 'not-allowed'}`}>
                {stamp.studyRights.allowed ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                {stamp.studyRights.allowed ? 'Allowed' : 'Not Allowed'}
              </div>
              <p>{stamp.studyRights.details}</p>
            </div>
          )}
        </div>
        
        {/* Family Reunification */}
        <div className="details-section">
          <button className="section-toggle" onClick={() => toggleSection('family')}>
            <Users size={18} />
            Family Reunification
            {expandedSection === 'family' ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          {expandedSection === 'family' && (
            <div className="section-content">
              <div className={`status-badge ${stamp.familyReunification.allowed ? 'allowed' : 'not-allowed'}`}>
                {stamp.familyReunification.allowed ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                {stamp.familyReunification.allowed ? 'Allowed' : 'Not Allowed'}
              </div>
              <p>{stamp.familyReunification.details}</p>
            </div>
          )}
        </div>
        
        {/* Required Documents */}
        <div className="details-section">
          <button className="section-toggle" onClick={() => toggleSection('documents')}>
            <FileText size={18} />
            Required Documents
            {expandedSection === 'documents' ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          {expandedSection === 'documents' && (
            <div className="section-content">
              <ul className="document-list">
                {stamp.requiredDocuments.map((doc, i) => (
                  <li key={i}><CheckCircle size={14} /> {doc}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        
        {/* Fees & Processing */}
        <div className="details-section">
          <button className="section-toggle" onClick={() => toggleSection('fees')}>
            <DollarSign size={18} />
            Fees & Processing
            {expandedSection === 'fees' ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          {expandedSection === 'fees' && (
            <div className="section-content">
              <div className="fee-row">
                <span className="fee-label">Fees:</span>
                <span className="fee-value">{stamp.fees}</span>
              </div>
              <div className="fee-row">
                <span className="fee-label">Processing Time:</span>
                <span className="fee-value">{stamp.processingTime}</span>
              </div>
              <p className="fee-disclaimer">
                Fees and processing times are indicative only. Confirm current registration, permit and visa charges with the official
                immigration or employment-permit authority.
              </p>
            </div>
          )}
        </div>
        
        {/* Path to Next */}
        {stamp.nextStamps.length > 0 && (
          <div className="path-to-next">
            <h4>Path to Next Stamp</h4>
            <p>{stamp.pathToNext}</p>
            <div className="next-stamps">
              {stamp.nextStamps.map(nextId => (
                <button
                  key={nextId}
                  className="next-stamp-btn"
                  style={{ borderColor: STAMP_DATA[nextId]?.color, color: STAMP_DATA[nextId]?.color }}
                  onClick={() => {
                    setSelectedStamp(nextId);
                    setShowTransition(`${stamp.id}-to-${nextId}`);
                  }}
                >
                  → {STAMP_DATA[nextId]?.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTransitionDetails = () => {
    if (!showTransition) return null;
    
    const transition = TRANSITION_REQUIREMENTS[showTransition];
    if (!transition) return null;
    
    const fromStamp = STAMP_DATA[transition.from];
    const toStamp = STAMP_DATA[transition.to];
    
    return (
      <div className="transition-overlay">
        <div className="transition-modal">
          <button className="close-btn" onClick={() => setShowTransition(null)}>×</button>
          
          <h3>Transition Requirements</h3>
          
          <div className="transition-path">
            <span style={{ color: fromStamp.color }}>{fromStamp.name}</span>
            <ArrowRight size={20} />
            <span style={{ color: toStamp.color }}>{toStamp.name}</span>
          </div>
          
          <div className="transition-difficulty" style={{ 
            backgroundColor: transition.difficulty === 'Easy' ? 'rgba(16, 185, 129, 0.2)' :
                           transition.difficulty === 'Medium' ? 'rgba(245, 158, 11, 0.2)' :
                           'rgba(239, 68, 68, 0.2)',
            color: transition.difficulty === 'Easy' ? '#10b981' :
                   transition.difficulty === 'Medium' ? '#f59e0b' : '#ef4444'
          }}>
            Difficulty: {transition.difficulty}
          </div>
          
          <div className="transition-section">
            <h4>Requirements</h4>
            <ul>
              {transition.requirements.map((req, i) => (
                <li key={i}><CheckCircle size={14} /> {req}</li>
              ))}
            </ul>
          </div>
          
          <div className="transition-section">
            <h4>Required Documents</h4>
            <ul>
              {transition.documents.map((doc, i) => (
                <li key={i}><FileText size={14} /> {doc}</li>
              ))}
            </ul>
          </div>
          
          <div className="transition-info">
            <div className="info-item">
              <Clock size={16} />
              <span>Processing: {transition.processingTime}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTimeline = () => (
    <div className="timeline-container">
      <h3>
        <Calendar size={20} />
        Visa Journey Timeline
      </h3>
      
      <div className="timeline-slider">
        <input
          type="range"
          min="0"
          max="15"
          value={timelineYear}
          onChange={(e) => setTimelineYear(parseInt(e.target.value))}
          className="slider"
        />
        <div className="timeline-labels">
          <span>Year 0</span>
          <span>Year 5</span>
          <span>Year 10</span>
          <span>Year 15+</span>
        </div>
      </div>
      
      <div className="timeline-milestones">
        {TIMELINE_MILESTONES.map((milestone, i) => (
          <div
            key={i}
            className={`milestone ${timelineYear >= milestone.year ? 'reached' : ''} ${timelineYear === milestone.year ? 'current' : ''}`}
            style={{ 
              left: `${(milestone.year / 15) * 100}%`,
              '--milestone-color': STAMP_DATA[milestone.stamp]?.color
            }}
            onClick={() => setTimelineYear(milestone.year)}
          >
            <div className="milestone-dot" />
            <div className="milestone-label">
              <span className="year">Year {milestone.year}</span>
              <span className="event">{milestone.label}</span>
            </div>
          </div>
        ))}
      </div>
      
      <div className="current-year-info">
        <p>Year {timelineYear}: {
          TIMELINE_MILESTONES.find(m => m.year === timelineYear)?.label || 'Your journey continues...'
        }</p>
      </div>
    </div>
  );

  const renderPathCalculator = () => (
    <div className="path-calculator">
      <h3>
        <Target size={20} />
        Your Path Calculator
      </h3>
      
      <div className="current-stamp-selector">
        <label>Select your current stamp:</label>
        <div className="stamp-options">
          {Object.keys(STAMP_DATA).filter(s => !['stamp-3', 'stamp-5', 'citizenship'].includes(s)).map(stampId => {
            const stamp = STAMP_DATA[stampId];
            const IconComponent = stamp.icon;
            return (
              <button
                key={stampId}
                className={`stamp-option ${currentStamp === stampId ? 'selected' : ''}`}
                style={{ '--stamp-color': stamp.color }}
                onClick={() => setCurrentStamp(stampId)}
              >
                <IconComponent size={18} />
                {stamp.name}
              </button>
            );
          })}
        </div>
      </div>
      
      {currentStamp && (
        <>
          <div className="calculated-path">
            <h4>Your Path to Citizenship:</h4>
            <div className="path-flow">
              {calculatedPath.map((stampId, i) => (
                <React.Fragment key={stampId}>
                  {renderStampCard(stampId, true, i === 0)}
                </React.Fragment>
              ))}
            </div>
          </div>
          
          {yearsToPR && (
            <div className="time-estimates">
              <div className="estimate-card">
                <div className="estimate-icon" style={{ backgroundColor: 'rgba(22, 155, 98, 0.2)', color: '#169B62' }}>
                  <Home size={24} />
                </div>
                <div className="estimate-info">
                  <span className="estimate-value">{yearsToPR.yearsToStamp4} years</span>
                  <span className="estimate-label">to Stamp 4 permission</span>
                </div>
              </div>
              
              <div className="estimate-card">
                <div className="estimate-icon" style={{ backgroundColor: 'rgba(6, 182, 212, 0.2)', color: '#06b6d4' }}>
                  <Building size={24} />
                </div>
                <div className="estimate-info">
                  <span className="estimate-value">{yearsToPR.yearsToPR} years</span>
                  <span className="estimate-label">to Stamp 5 permission</span>
                </div>
              </div>
              
              <div className="estimate-card">
                <div className="estimate-icon" style={{ backgroundColor: 'rgba(255, 136, 51, 0.2)', color: '#FF8833' }}>
                  <Award size={24} />
                </div>
                <div className="estimate-info">
                  <span className="estimate-value">{yearsToPR.yearsToCitizenship} years</span>
                  <span className="estimate-label">to Citizenship Eligible</span>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="stamp-visualizer">
      <div className="visualizer-header">
        <h2>🇮🇪 Ireland Stamp Transition Visualizer</h2>
        <p className="visualizer-subtitle">
          Interactive planning guide to immigration permission pathways in Ireland
        </p>
        <p className="visualizer-disclaimer">Pathways, residence calculations, fees, and timelines are illustrative. Confirm the current permission and employment-permit rules with the official authorities.</p>
      </div>

      {/* Main Flowchart */}
      <div className="flowchart-section">
        <h3>
          <ArrowRight size={20} />
          Visa Stamp Pathways
        </h3>
        
        <div className="flowchart">
          {/* Main Path */}
          <div className="flow-path main-path">
            <div className="path-label">Standard Student Path</div>
            <div className="flow-nodes">
              {renderStampCard('stamp-2')}
              {renderStampCard('stamp-1g')}
              {renderStampCard('stamp-1')}
              {renderStampCard('stamp-4')}
              {renderStampCard('stamp-5')}
              {renderStampCard('citizenship')}
            </div>
          </div>
          
          {/* Critical Skills Fast Track */}
          <div className="flow-path fast-track">
            <div className="path-label">Critical Skills pathway</div>
            <div className="flow-nodes">
              {renderStampCard('stamp-2')}
              {renderStampCard('stamp-1g')}
              {renderStampCard('stamp-4-critical')}
              {renderStampCard('stamp-4')}
              {renderStampCard('stamp-5')}
              {renderStampCard('citizenship')}
            </div>
          </div>
          
          {/* Dependent Path */}
          <div className="flow-path dependent-path">
            <div className="path-label">Dependent Path</div>
            <div className="flow-nodes">
              {renderStampCard('stamp-3')}
              {renderStampCard('stamp-4')}
              {renderStampCard('stamp-5')}
              {renderStampCard('citizenship')}
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      {renderTimeline()}

      {/* Path Calculator */}
      {renderPathCalculator()}

      {/* Stamp Details Panel */}
      {selectedStamp && renderStampDetails()}

      {/* Transition Details Modal */}
      {renderTransitionDetails()}

      {/* Info Footer */}
      <div className="info-footer">
        <Info size={16} />
        <p>
          This is a general guide. Individual circumstances may vary. 
          Always check official Irish immigration resources for the most current requirements.
        </p>
      </div>
    </div>
  );
};

export default IrelandStampVisualizer;
